import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { AdminShell } from "./AdminShell";
import { useBrandCtx } from "../../context/BrandContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

const TEXT_FIELDS = [
  ["business_name", "Business Name"],
  ["product_name", "Product Name"],
  ["advisor_name", "Advisor Name"],
  ["mobile", "Mobile Number"],
  ["whatsapp_number", "WhatsApp Number (with country code, e.g. 919876543210)"],
  ["email", "Email"],
  ["website", "Website"],
  ["address", "Address"],
  ["logo_url", "Logo URL"],
  ["tagline", "Main Tagline"],
  ["secondary_tagline", "Secondary Tagline"],
];

const AREA_FIELDS = [
  ["disclaimer", "Disclaimer"],
  ["privacy_policy", "Privacy Policy"],
  ["report_footer", "Report Footer"],
  ["whatsapp_template", "WhatsApp Message Template ({name}, {score}, {priority1..3}, {advisor}, {business})"],
];

const SOCIAL = ["facebook", "instagram", "linkedin", "twitter"];

export default function AdminSettings() {
  const { settings, setSettings } = useBrandCtx();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => setForm({ social_links: {}, ...res.data }));
  }, []);

  if (!form) return <AdminShell title="Settings"><div className="grid place-items-center py-20 text-navy"><Loader2 className="animate-spin" /></div></AdminShell>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...(f.social_links || {}), [k]: v } }));
  const setLS = (k, v) => setForm((f) => ({ ...f, lead_scoring: { ...(f.lead_scoring || {}), [k]: Number(v) } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", { data: form });
      setSettings((prev) => ({ ...prev, ...res.data }));
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <AdminShell title="Settings" actions={
      <button data-testid="settings-save" onClick={save} disabled={saving}
        className="rounded-full bg-gold text-navy text-sm font-heading font-bold px-5 py-2 flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save
      </button>
    }>
      <div className="max-w-3xl space-y-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
          <div className="font-heading font-bold text-navy mb-4">Brand & Contact</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {TEXT_FIELDS.map(([k, label]) => (
              <div key={k} className={k === "secondary_tagline" || k === "logo_url" ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-semibold text-slate-600">{label}</Label>
                <Input data-testid={`set-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1.5 rounded-lg" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
          <div className="font-heading font-bold text-navy mb-4">Social Links</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {SOCIAL.map((k) => (
              <div key={k}>
                <Label className="text-xs font-semibold text-slate-600 capitalize">{k}</Label>
                <Input data-testid={`social-${k}`} value={form.social_links?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)} className="mt-1.5 rounded-lg" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
          <div className="font-heading font-bold text-navy mb-1">Lead Scoring</div>
          <p className="text-xs text-slate-500 mb-4">Tune the internal points thresholds that classify a lead as HOT or WARM. Higher points = stronger investment capacity, more gaps and clear goals.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">HOT — min points</Label>
              <Input data-testid="ls-hot-points" type="number" value={form.lead_scoring?.hot_points ?? 14} onChange={(e) => setLS("hot_points", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">HOT — min investment capacity (0-6)</Label>
              <Input data-testid="ls-hot-capacity" type="number" min="0" max="6" value={form.lead_scoring?.hot_min_capacity ?? 3} onChange={(e) => setLS("hot_min_capacity", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">WARM — min points</Label>
              <Input data-testid="ls-warm-points" type="number" value={form.lead_scoring?.warm_points ?? 8} onChange={(e) => setLS("warm_points", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Capacity scale: ₹0=0, &lt;5k=1, 5–10k=2, 10–25k=3, 25–50k=4, 50k–1L=5, &gt;1L=6. New leads use these thresholds; existing leads keep their prior classification.</p>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
          <div className="font-heading font-bold text-navy mb-4">Content & Templates</div>
          <div className="space-y-4">
            {AREA_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs font-semibold text-slate-600">{label}</Label>
                <Textarea data-testid={`set-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1.5 rounded-lg" rows={k === "disclaimer" || k === "whatsapp_template" ? 5 : 3} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
